/**
 * ProfileCompletenessBanner — iter1089 — Dashboard banner прикрит до 80% попълнен.
 *
 * Показва процент попълненост + следваща стъпка + deep-link към секцията.
 * Изчезва автоматично щом бъдат попълнени ≥80%. Не е dismissable — за
 * да не остане потребител „заключен“ без функции без да знае защо.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { ArrowRight, Sparkles, PenLine } from "lucide-react";
import { computeCompleteness } from "@/lib/profileCompleteness";
import ProfileQuickEditForm from "@/components/ProfileQuickEditForm";
import axios from "axios";

export default function ProfileCompletenessBanner() {
  const { user, API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [data, setData] = React.useState(null);
  const [dismissed, setDismissed] = React.useState(false);
  // iter1093 — Quick-edit inline form (toggle-нат от bookmark бутон)
  const [quickEditOpen, setQuickEditOpen] = React.useState(false);

  React.useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [profileRes, twinRes] = await Promise.all([
          axios.get(`${API}/auth/me`).catch(() => ({ data: user })),
          axios.get(`${API}/health-twin/twin-score`).catch(() => ({ data: null })),
        ]);
        const p = profileRes.data?.profile || {};
        // iter1092 — twin-score endpoint връща `coverage_pct` + `sub_scores`,
        // а не `twins` array. Използваме coverage_pct директно.
        const twin_readiness_pct = twinRes.data?.coverage_pct || 0;
        // iter1092 — /lab-snapshots не съществува. Броим от profile.labValues
        // или profile.labs (real shape от /auth/me).
        const lab_snapshots_count =
          (Array.isArray(p.labValues) ? p.labValues.length : 0)
          || (Array.isArray(p.labs) ? p.labs.length : 0);
        setData({
          ...p,
          twin_readiness_pct,
          lab_snapshots_count,
        });
      } catch (e) {
        // Silently fail — banner е cosmetic.
      }
    };
    load();
  }, [user, API]);

  if (!data || dismissed) return null;
  // iter1092 — profile-a е nested → gender живее в personal.gender.
  const sex = data?.personal?.gender || data?.gender || data?.sex;
  const { pct, sections } = computeCompleteness(data, sex);
  if (pct >= 80) return null;   // hide when profile is complete-enough

  const nextSection = sections.find((s) => s.applicable && !s.complete);

  return (
    <div
      data-testid="profile-completeness-banner"
      className={`rounded-2xl border p-4 sm:p-5 mb-4 ${
        isDark
          ? "bg-gradient-to-br from-[hsl(268,60%,20%)] to-[hsl(223,47%,10%)] border-[hsl(268,50%,35%)]"
          : "bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className={`w-4 h-4 ${isDark ? "text-purple-300" : "text-purple-600"}`} />
            <span className={`text-xs font-semibold uppercase tracking-wide ${isDark ? "text-purple-300" : "text-purple-700"}`}>
              {bg ? "Профилът Ви" : "Your profile"} — {pct}% {bg ? "попълнен" : "complete"}
            </span>
          </div>
          <h3 className={`text-sm sm:text-base font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
            {nextSection
              ? (bg
                ? `Попълнете „${nextSection.label_bg}“ за да отключите:`
                : `Fill in "${nextSection.label_en}" to unlock:`)
              : (bg ? "Малко остава!" : "Almost done!")}
          </h3>
          {nextSection?.unlocks_bg?.length ? (
            <ul className={`text-xs mt-1.5 space-y-0.5 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              {nextSection.unlocks_bg.map((u) => (
                <li key={u} data-testid={`banner-unlock-${u.replace(/\s+/g, "-")}`}>• {u}</li>
              ))}
            </ul>
          ) : null}
        </div>
        {nextSection ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setQuickEditOpen((v) => !v)}
              data-testid="banner-quick-edit-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold ${
                isDark
                  ? "bg-white/10 hover:bg-white/15 text-purple-200"
                  : "bg-white text-purple-700 border border-purple-300 hover:bg-purple-50"
              }`}
            >
              <PenLine className="w-3.5 h-3.5" />
              {quickEditOpen
                ? (bg ? "Затвори" : "Close")
                : (bg ? "Попълни бързо" : "Quick fill")}
            </button>
            <Link
              to={nextSection.action_route}
              data-testid="banner-cta"
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold shrink-0 ${
                isDark ? "bg-purple-500 hover:bg-purple-400 text-white" : "bg-purple-600 hover:bg-purple-700 text-white"
              }`}
            >
              {bg ? "Пълна форма" : "Full form"} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        ) : null}
      </div>
      <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-white/60"}`}>
        <div
          data-testid="banner-progress"
          className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {/* Section chips */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {sections.filter(s => s.applicable).map((s) => (
          <span
            key={s.key}
            data-testid={`banner-section-${s.key}`}
            className={`text-[10px] px-2 py-0.5 rounded-full ${
              s.complete
                ? (isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700")
                : (isDark ? "bg-white/5 text-slate-400" : "bg-white text-slate-500")
            }`}
          >
            {s.complete ? "✓ " : "○ "}{bg ? s.label_bg : s.label_en}
          </span>
        ))}
      </div>
      {/* iter1093 — Quick-edit form: попълвай 3 полета директно от banner-a. */}
      {quickEditOpen && (
        <ProfileQuickEditForm
          profile={data}
          onSaved={() => setQuickEditOpen(false)}
        />
      )}
    </div>
  );
}
